/**
 * MR JERRY CLUB — Główny wrapper publicznej aplikacji lojalnościowej.
 *
 * Route: /mrjerryclub/*
 *
 * Stany:
 *   - checking: spinner while /api/club/auth/me is in flight
 *   - guest: not logged in → ClubLanding (login/register tabs)
 *   - member: logged in → app shell with bottom nav (Dashboard / Paragony / Profil)
 */
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { clubApi } from './clubApi';
import ClubLanding from './ClubLanding';
import ClubDashboard from './ClubDashboard';
import ClubAddReceipt from './ClubAddReceipt';
import ClubReceipts from './ClubReceipts';
import ClubProfile from './ClubProfile';
import ClubRewards from './ClubRewards';
import ClubCoupons from './ClubCoupons';
import { ClubPromotions, ClubReviewForm, ClubIdeaForm } from './ClubEngagement';
import './club.css';

const ClubAuthContext = createContext(null);

export const useClubAuth = () => useContext(ClubAuthContext);

export default function ClubApp() {
  const [state, setState] = useState({ status: 'checking', member: null });
  const navigate = useNavigate();
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      const { data } = await clubApi.get('/auth/me');
      setState({ status: 'member', member: data.member });
    } catch {
      setState({ status: 'guest', member: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleLogout = async () => {
    try { await clubApi.post('/auth/logout'); } catch (_e) { /* ignore */ }
    setState({ status: 'guest', member: null });
    navigate('/mrjerryclub', { replace: true });
  };

  if (state.status === 'checking') {
    return (
      <div className="club-root">
        <div className="club-shell">
          <div className="club-loading">
            <div className="club-loading-spinner" />
            <div style={{ marginTop: 12 }}>Ładowanie…</div>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === 'guest') {
    return (
      <div className="club-root">
        <ClubLanding onAuthenticated={(member) => {
          setState({ status: 'member', member });
          navigate('/mrjerryclub/dashboard', { replace: true });
        }} />
      </div>
    );
  }

  // === MEMBER (logged in) ===
  const ctx = { member: state.member, refresh, logout: handleLogout };

  // Determine active tab from URL
  const path = location.pathname.replace(/\/$/, '');
  let activeTab = 'dashboard';
  if (path.includes('/dodaj-paragon')) activeTab = 'add';
  else if (path.includes('/paragony')) activeTab = 'receipts';
  else if (path.includes('/nagrody')) activeTab = 'rewards';
  else if (path.includes('/kupony')) activeTab = 'coupons';
  else if (path.includes('/profil')) activeTab = 'profile';

  return (
    <ClubAuthContext.Provider value={ctx}>
      <div className="club-root">
        <Routes>
          <Route path="" element={<ClubDashboard />} />
          <Route path="dashboard" element={<ClubDashboard />} />
          <Route path="dodaj-paragon" element={<ClubAddReceipt />} />
          <Route path="paragony" element={<ClubReceipts />} />
          <Route path="nagrody" element={<ClubRewards />} />
          <Route path="kupony" element={<ClubCoupons />} />
          <Route path="promocje" element={<ClubPromotions />} />
          <Route path="ocen" element={<ClubReviewForm />} />
          <Route path="pomysl" element={<ClubIdeaForm />} />
          <Route path="profil" element={<ClubProfile />} />
        </Routes>

        {/* Bottom navigation — 5 zakładek */}
        <nav className="club-bottom-nav" data-testid="club-bottom-nav">
          <button
            className={`club-bottom-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => navigate('/mrjerryclub/dashboard')}
            data-testid="club-nav-dashboard"
          >
            <span className="club-bottom-tab-icon">🏠</span>
            <span>Start</span>
          </button>
          <button
            className={`club-bottom-tab ${activeTab === 'rewards' ? 'active' : ''}`}
            onClick={() => navigate('/mrjerryclub/nagrody')}
            data-testid="club-nav-rewards"
          >
            <span className="club-bottom-tab-icon">🎁</span>
            <span>Nagrody</span>
          </button>
          <button
            className={`club-bottom-tab ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => navigate('/mrjerryclub/dodaj-paragon')}
            data-testid="club-nav-add"
          >
            <span className="club-bottom-tab-icon">📸</span>
            <span>Paragon</span>
          </button>
          <button
            className={`club-bottom-tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => navigate('/mrjerryclub/kupony')}
            data-testid="club-nav-coupons"
          >
            <span className="club-bottom-tab-icon">🎟️</span>
            <span>Kupony</span>
          </button>
          <button
            className={`club-bottom-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => navigate('/mrjerryclub/profil')}
            data-testid="club-nav-profile"
          >
            <span className="club-bottom-tab-icon">👤</span>
            <span>Profil</span>
          </button>
        </nav>
      </div>
    </ClubAuthContext.Provider>
  );
}
